import { useState, useEffect, useRef } from 'react';
import { checkVersion, isUpdateAvailable, getCurrentVersion, initializeVersion, getVersionCheckConfig } from '../services/versionService';

const useVersionChecker = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [serverVersion, setServerVersion] = useState(null);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const intervalRef = useRef(null);

  const performVersionCheck = async () => {
    if (isChecking) return; // Prevent multiple simultaneous checks
    
    try {
      setIsChecking(true);
      const versionInfo = await checkVersion();
      
      console.log('Version check response:', versionInfo); // Debug log
      
      if (versionInfo && versionInfo.has_update) {
        setServerVersion(versionInfo.latest_version);
        setUpdateAvailable(true);
        setNotificationOpen(true);
        console.log('Update available:', versionInfo.latest_version); // Debug log
      } else {
        // Check if user has updated to the latest version
        const lastUpdateVersion = localStorage.getItem('lastUpdateVersion');
        const currentVersion = getCurrentVersion();
        
        // Only reset updateAvailable if:
        // 1. We're not currently showing an update, OR
        // 2. User has successfully updated to the latest version
        if (!updateAvailable || (lastUpdateVersion && currentVersion === lastUpdateVersion)) {
          setUpdateAvailable(false);
          // Clear the stored version since we've confirmed the update
          if (lastUpdateVersion && currentVersion === lastUpdateVersion) {
            localStorage.removeItem('lastUpdateVersion');
          }
        }
        // Don't close notification if it's already open - let user dismiss it manually
        console.log('No update available'); // Debug log
      }
    } catch (error) {
      console.warn('Version check failed:', error);
      // Don't show notification on error to avoid false positives
    } finally {
      setIsChecking(false);
    }
  };

  const startVersionChecking = () => {
    const { CHECK_INTERVAL } = getVersionCheckConfig();
    
    // Perform initial check
    performVersionCheck();
    
    // Set up periodic checking
    intervalRef.current = setInterval(performVersionCheck, CHECK_INTERVAL);
  };

  const stopVersionChecking = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const handleReload = (hasPendingQueries = false) => {
    // Check if there are pending queries
    if (hasPendingQueries) {
      const confirmed = window.confirm(
        'You have a query in progress. Reloading now will cancel the current operation. Do you want to continue?'
      );
      if (!confirmed) {
        return;
      }
    }
    
    // Store the current server version to check if update was successful
    if (serverVersion) {
      localStorage.setItem('lastUpdateVersion', serverVersion);
    }
    
    // Reload the page to get the new version
    window.location.reload();
  };

  const handleCloseNotification = () => {
    setNotificationOpen(false);
  };

  const handleDismissNotification = () => {
    setNotificationOpen(false);
    // Optionally, you could store a flag to not show the notification again for this version
    // localStorage.setItem('version_notification_dismissed', serverVersion);
  };

  useEffect(() => {
    // Initialize version first, then start version checking
    const initVersion = async () => {
      await initializeVersion();
      startVersionChecking();
    };
    
    initVersion();

    // Cleanup on unmount
    return () => {
      stopVersionChecking();
    };
  }, []);

  // Also check for version updates when the page becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && !isChecking) {
        performVersionCheck();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isChecking]);

  return {
    updateAvailable,
    serverVersion,
    currentVersion: getCurrentVersion(),
    notificationOpen,
    isChecking,
    handleReload,
    handleCloseNotification,
    handleDismissNotification,
    performVersionCheck, // Expose for manual checking if needed
  };
};

export default useVersionChecker; 